// ENT-1580: shared onboarding repair-CTA derivation.
//
// These derivers were originally inline in OnboardBatchMonitor.tsx (ENT-1543).
// They are extracted here verbatim so the Onboard Batch Monitor grid AND the
// SecurityChecksAccordion (ENT-1580, admin-console-only) import ONE
// implementation instead of re-deriving repair vs deep-link per surface.
//
// Contract: a failed/repairable row → `kind: 'repair'` (inline call to the
// canonical admin-supplier-onboarding-repair RPC by the caller); an
// inspect-only / not-yet-failed action → `kind: 'link'` deep-linking into the
// owning surface under the shared diagnostics/queue query contract. Returns
// null when there is no action to offer.
//
// Pure functions only — no React, no side effects — so they are unit-testable
// and reusable across both surfaces.

import type {
  BatchMonitorDomain,
  BatchMonitorListing,
  BatchMonitorListingJob,
  BatchMonitorScan,
} from '@/hooks/useOnboardBatchMonitor';
import { isMarkedDeadDecisionOutcome } from '@/lib/supplierOnboardingOutcome';

export type CtaTone = 'default' | 'secondary' | 'outline' | 'destructive';

export type GridCta =
  | { kind: 'link'; label: string; href: string; tone: CtaTone }
  | { kind: 'repair'; label: string; listingId: string; tone: CtaTone };

// Build an /admin marketplace deep-link query string. Kept identical to the
// original inline helper so existing navigation/back-nav behaviour is preserved.
function marketplaceHref(extra: Record<string, string>): string {
  return `/admin?${new URLSearchParams({ tab: 'marketplace', ...extra }).toString()}`;
}

// ENT-1580 (accordion): the SecurityChecksAccordion builds admin deep-links via
// this same helper; exported so both surfaces share one URL builder.
export const adminMarketplaceHref = marketplaceHref;

// ENT-5456: deep link to ONE onboarding review case in the Review Cockpit
// (`?reviewId=` focuses that case, any status). Shared by the batch-monitor CTAs
// and the Scan Engine panel's operator-decision CTA.
export function onboardingReviewCaseHref(reviewId: string): string {
  return marketplaceHref({ marketplaceView: 'onboarding-review', reviewId });
}

// ENT-5456: a "Mark dead" review decision (operator or review automation)
// stores terminal_dns_no_host. Its CTA opens the review case that made the
// decision (reason_detail.review_id), not the listing a DNS failure links to.
function markedDeadDecisionCta(listing: BatchMonitorListing): Extract<GridCta, { kind: 'link' }> | null {
  if (!isMarkedDeadDecisionOutcome({ reason_code: listing.reasonCode, next_action: listing.nextAction, reason_detail: listing.reasonDetail })) return null;
  const reviewId = listing.reasonDetail?.review_id;
  if (typeof reviewId !== 'string' || reviewId.trim() === '') return null;
  return { kind: 'link', label: 'Open review case', tone: 'outline', href: onboardingReviewCaseHref(reviewId) };
}

function isDiscoveryQueueSyntheticListing(listing: BatchMonitorListing): boolean {
  return listing.listingId.startsWith('discovery:');
}

export function listingDeepLinkCta(listing: BatchMonitorListing, tone: CtaTone = 'outline'): Extract<GridCta, { kind: 'link' }> {
  if (isDiscoveryQueueSyntheticListing(listing)) {
    return {
      kind: 'link',
      label: 'Find listing',
      tone,
      href: marketplaceHref({
        marketplaceView: 'listings',
        marketplaceStatus: 'pending',
        action: 'filter-domain',
        listingDomain: listing.domain,
      }),
    };
  }

  return {
    kind: 'link',
    label: 'Open listing',
    tone,
    href: marketplaceHref({
      marketplaceView: 'listings',
      listingId: listing.listingId,
      action: 'open-source-row',
    }),
  };
}

export function listingCta(listing: BatchMonitorListing): GridCta | null {
  const id = listing.listingId;
  switch (listing.reasonCode) {
    case 'onboarding_succeeded':
      return listingDeepLinkCta(listing);
    case 'canonical_url_manual_review':
    case 'duplicate_org_link':
      if (isDiscoveryQueueSyntheticListing(listing)) return listingDeepLinkCta(listing, 'secondary');
      return { kind: 'link', label: 'Review URL', tone: 'secondary', href: marketplaceHref({ marketplaceView: 'listings', listingId: id, action: 'open-source-row' }) };
    case 'provider_retry_exhausted':
      return { kind: 'repair', label: 'Requeue scan', tone: 'default', listingId: id };
    case 'evidence_score_gap':
    case 'component_retry_exhausted':
      return { kind: 'repair', label: 'Retry now', tone: 'default', listingId: id };
    case 'terminal_dns_no_host':
      return markedDeadDecisionCta(listing) ?? listingDeepLinkCta(listing, 'destructive');
    default:
      if (
        isDiscoveryQueueSyntheticListing(listing) &&
        (listing.stages.input === 'failed' || listing.stages.discovery === 'failed')
      ) {
        return listingDeepLinkCta(listing);
      }
      return null;
  }
}

export function listingJobCta(listing: BatchMonitorListing, job: BatchMonitorListingJob): GridCta | null {
  if (job.status === 'succeeded' || job.status === 'running' || job.status === 'not_started') return null;
  const id = listing.listingId;
  if (job.stage === 'input' || job.stage === 'discovery') {
    if (isDiscoveryQueueSyntheticListing(listing) || listing.reasonCode === 'terminal_dns_no_host') {
      return markedDeadDecisionCta(listing) ?? listingDeepLinkCta(listing);
    }
    return { kind: 'link', tone: 'outline', label: 'Open discovery queue', href: marketplaceHref({ marketplaceView: 'queue-monitor', queueType: 'discovery', listingId: id, ...(job.status === 'failed' ? { status: 'failed' } : {}) }) };
  }
  if (job.stage === 'domains') {
    if (job.status === 'failed') return { kind: 'repair', tone: 'outline', label: 'Requeue scans', listingId: id };
    return { kind: 'link', tone: 'outline', label: 'Open scan queue', href: marketplaceHref({ marketplaceView: 'queue-monitor', queueType: 'scan', listingId: id }) };
  }
  if (job.stage === 'score') {
    return { kind: 'repair', tone: 'outline', label: 'Recalculate score', listingId: id };
  }
  return { kind: 'link', tone: 'outline', label: 'Open diagnostics', href: marketplaceHref({ marketplaceView: 'onboarding-diagnostics', listingId: id }) };
}

export function domainCta(listing: BatchMonitorListing, domain: BatchMonitorDomain): GridCta | null {
  const score = domain.stages.score;
  if (score !== 'failed' && score !== 'blocked' && score !== 'needs_review' && score !== 'pending') return null;
  const failed = score === 'failed' || score === 'blocked';
  if (failed) return { kind: 'repair', tone: 'secondary', label: 'Requeue domain scans', listingId: listing.listingId };
  return {
    kind: 'link', tone: 'secondary', label: 'Open queue',
    href: marketplaceHref({ marketplaceView: 'queue-monitor', queueType: 'scan', listingId: listing.listingId, domain: domain.domain }),
  };
}

export function scanCta(listing: BatchMonitorListing, domain: BatchMonitorDomain, scan: BatchMonitorScan): GridCta | null {
  if (scan.status === 'succeeded' || scan.status === 'running' || scan.status === 'not_started') return null;
  if (scan.status === 'failed') return { kind: 'repair', tone: 'outline', label: 'Retry scan', listingId: listing.listingId };
  return {
    kind: 'link', tone: 'outline', label: 'Open queue',
    href: marketplaceHref({ marketplaceView: 'queue-monitor', queueType: 'scan', listingId: listing.listingId, domain: domain.domain, scanType: scan.scanType }),
  };
}
